import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../lib/api";
import { formatDisplayName } from "../lib/nameFormat";

interface RosterEntry {
  player_id: string;
  player_name: string;
  jersey_number: number;
  position: string;
}

interface TeamData {
  team_id: string;
  name: string;
  short_name?: string;
  gender?: string;
  country_name?: string;
}

interface TeamPlayerResponse {
  player_id: string;
  player_name: string;
  jersey_number: number;
  position: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export default function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const { t } = useTranslation("teams");
  const [team, setTeam] = useState<TeamData | null>(null);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    apiClient
      .get<TeamData>(`teams/${teamId}`)
      .then((data) => {
        setTeam(data);
        // Fetch the roster separately — TeamResponse does not include roster
        return apiClient
          .get<PaginatedResponse<TeamPlayerResponse>>(
            `teams/${teamId}/players?page=1&page_size=100`,
          )
          .then((rosterData) => {
            setRoster(
              rosterData.items.map((entry) => ({
                player_id: entry.player_id,
                player_name: entry.player_name,
                jersey_number: entry.jersey_number,
                position: entry.position,
              })),
            );
          })
          .catch(() => {
            // Roster fetch failed — show empty state, don't crash
            setRoster([]);
          });
      })
      .catch(() => {
        setError(t("error"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [teamId, t]);

  if (loading) {
    return (
      <div
        data-testid="team-detail-loading"
        className="min-h-screen flex items-center justify-center bg-gray-50"
      >
        <p className="text-sm text-gray-500">{t("loading")}</p>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div
        data-testid="team-detail-error"
        className="min-h-[60vh] flex flex-col items-center justify-center py-16 px-4 text-center"
      >
        <p className="text-xl font-semibold text-gray-900 mb-2">
          {error ?? t("error")}
        </p>
        <Link
          to="/teams"
          data-testid="team-detail-back"
          className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100 mt-4"
        >
          {t("allTeams")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="team-detail-page" className="p-8">
      <div className="mb-4">
        <Link
          to="/teams"
          data-testid="team-detail-back"
          className="text-sm text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
        >
          &larr; {t("allTeams")}
        </Link>
      </div>

      <h1
        data-testid="team-detail-name"
        className="text-3xl font-bold text-gray-900 mb-8"
      >
        {team.name}
      </h1>

      {/* Team info */}
      <div data-testid="team-detail-info" className="card mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t("teamDetails")}
        </h2>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          {team.short_name && (
            <div>
              <dt className="text-gray-500">{t("shortName")}</dt>
              <dd
                data-testid="team-detail-short-name"
                className="font-medium text-gray-900"
              >
                {team.short_name}
              </dd>
            </div>
          )}
          {team.country_name && (
            <div>
              <dt className="text-gray-500">{t("country")}</dt>
              <dd
                data-testid="team-detail-country"
                className="font-medium text-gray-900"
              >
                {team.country_name}
              </dd>
            </div>
          )}
          {team.gender && (
            <div>
              <dt className="text-gray-500">{t("gender")}</dt>
              <dd
                data-testid="team-detail-gender"
                className="font-medium text-gray-900"
              >
                {team.gender}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Roster */}
      <div data-testid="team-detail-roster" className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t("players")}
        </h2>
        {roster.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <p
              data-testid="team-detail-no-players"
              className="text-sm text-gray-500"
            >
              {t("noPlayers")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {roster.map((entry) => (
              <li
                key={entry.player_id}
                data-testid={`team-detail-player-${entry.player_id}`}
                className="py-3 flex items-center gap-4"
              >
                <span className="w-8 text-center font-bold text-gray-500">
                  {entry.jersey_number}
                </span>
                <span className="flex-1 font-medium text-gray-900">
                  {formatDisplayName(entry.player_name)}
                </span>
                <span className="text-xs text-gray-500">{entry.position}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
